import { IntlShape } from "react-intl";
import { FieldError, UseFormReturn } from "react-hook-form";

import type { GetServiceDetailsConfigResponseType } from "api/response";
import { AttachmentBucket } from "types/Common";
import { IFieldProps } from "types/Organisational";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { getRequestFormSections } from "@RequestFormV2/constants";
import type { RequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { getSectionKeyForFieldPath } from "@RequestFormV2/utils/getSectionKeyForFieldPath";
import { validateSchedule } from "@RequestFormV2/sections/Schedule/validation/validateSchedule";
import { validateServiceRequirements } from "@RequestFormV2/sections/Service/validation/validateServiceRequirements";
import { validateDatetimeChain } from "@RequestFormV2/validation/validateDatetimeChain";
import { validateOrganisation } from "@RequestFormV2/validation/sections/validateOrganisation";
import { validateService } from "@RequestFormV2/validation/sections/validateService";
import { validatePackingList } from "@RequestFormV2/validation/sections/validatePackingList";
import { validateProvider } from "@RequestFormV2/validation/sections/validateProvider";
import type { HandlingUnitHashMap } from "@RequestFormV2/sections/PackingList/types";
import { getHasMultipleCountries } from "@RequestFormV2/validation/getHasMultipleCountries";

export type ReviewFieldErrorSnapshot = {
  path: string;
  error: FieldError;
};

export type ValidateOnReviewConfig = {
  organisationFields: IFieldProps[];
  invoiceLocationDetails?: { mandatory?: boolean };
  shipmentTitleDetails?: { mandatory?: boolean };
  selectedLocation?: string;
  serviceDetailsConfig?: GetServiceDetailsConfigResponseType;
  showConsigneeSection: boolean;
  hasMultipleCountries: boolean;
  handlingUnitHashMap: HandlingUnitHashMap;
  nmfcNumberEnabled?: string | null;
  isParcelFreight: boolean;
  bucketAttachments: AttachmentBucket[];
  hasAttachmentBuckets: boolean;
  isOptionalPortAddressFormEnabled?: boolean;
  isEditShipment?: boolean;
  mode: RequestFormMode;
  quotingDeadlineMinimumMinutes?: number | null;
  preferredTimezone?: string | null;
};

export type ValidateOnReviewResult = {
  isValid: boolean;
  erroredSections: string[];
  fieldErrors: ReviewFieldErrorSnapshot[];
};

export function validateOnReview(
  methods: UseFormReturn,
  intl: IntlShape,
  config: ValidateOnReviewConfig,
): ValidateOnReviewResult {
  const { getValues, setError, clearErrors } = methods;
  const errorPaths: string[] = [];
  const fieldErrors: ReviewFieldErrorSnapshot[] = [];
  const sectionKeys = new Set(getRequestFormSections(config.mode).map(({ key }) => key));
  const hasProviderSection = sectionKeys.has("request-form.provider");
  const hasAttachmentsSection = sectionKeys.has("request-form.attachments");

  const trackSetError: typeof setError = (name, error, options) => {
    errorPaths.push(name);
    if (error && typeof error === "object" && "message" in error && error.message) {
      fieldErrors.push({ path: name, error: error as FieldError });
    }
    return setError(name, error, options);
  };

  clearErrors();

  let isValid = true;

  if (
    !validateOrganisation(getValues, trackSetError, intl, {
      organisationFields: config.organisationFields,
      invoiceLocationDetails: config.invoiceLocationDetails,
      shipmentTitleDetails: config.shipmentTitleDetails,
      selectedLocation: config.selectedLocation,
    })
  ) {
    isValid = false;
  }

  if (
    !validateService(getValues, trackSetError, intl, config.serviceDetailsConfig, {
      skipFreightMode: !hasProviderSection,
    })
  ) {
    isValid = false;
  }

  const serviceOptions = config.serviceDetailsConfig?.service_options;
  if (
    !validateServiceRequirements(
      methods,
      intl,
      serviceOptions?.request_service_requirement,
      serviceOptions?.request_service_requirement_values,
    )
  ) {
    isValid = false;
    serviceOptions?.request_service_requirement_values?.forEach((item) => {
      const valuePath = `service.special_requirements.${item.value}.value`;
      const requirementValue = methods.getValues(valuePath);

      if (requirementValue !== "true" && requirementValue !== "false") {
        errorPaths.push(valuePath);
      } else if (requirementValue === "true") {
        errorPaths.push(`service.special_requirements.${item.value}.additional_information`);
      }
    });
  }

  const stops = getValues("schedule.stops") as ScheduleStopValue[] | undefined;
  const hasMultipleCountries = config.hasMultipleCountries || getHasMultipleCountries(stops);

  if (
    !validateSchedule(getValues, trackSetError, clearErrors, intl, {
      showConsigneeSection: config.showConsigneeSection,
      hasMultipleCountries,
      isOptionalPortAddressFormEnabled: config.isOptionalPortAddressFormEnabled,
      askClearance: serviceOptions?.ask_clearance,
      mode: config.mode,
    })
  ) {
    isValid = false;
  }

  if (hasProviderSection && !validateDatetimeChain(getValues, trackSetError, intl, clearErrors)) {
    isValid = false;
  }

  if (
    !validatePackingList(getValues, trackSetError, intl, config.handlingUnitHashMap, {
      isParcelFreight: config.isParcelFreight,
      nmfcNumberEnabled: config.nmfcNumberEnabled,
    }).isValid
  ) {
    isValid = false;
  }

  if (hasProviderSection) {
    if (
      !validateProvider(getValues, trackSetError, intl, {
        bucketAttachments: config.bucketAttachments,
        hasAttachmentBuckets: config.hasAttachmentBuckets,
        skipProviderSelection: config.isEditShipment,
        skipDeadlines: false,
        quotingDeadlineMinimumMinutes: config.quotingDeadlineMinimumMinutes,
        preferredTimezone: config.preferredTimezone,
      })
    ) {
      isValid = false;
    }
  }

  if (hasAttachmentsSection) {
    if (
      !validateProvider(getValues, trackSetError, intl, {
        bucketAttachments: config.bucketAttachments,
        hasAttachmentBuckets: config.hasAttachmentBuckets,
        skipProviderSelection: true,
        skipDeadlines: true,
      })
    ) {
      isValid = false;
    }
  }

  const sectionsFromPaths = errorPaths.reduce<Set<string>>((acc, path) => {
    const section = getSectionKeyForFieldPath(path, config.mode);
    if (section) acc.add(section);
    return acc;
  }, new Set());

  const erroredSections = getRequestFormSections(config.mode)
    .map(({ key }) => key)
    .filter((key) => sectionsFromPaths.has(key));

  return {
    isValid,
    erroredSections,
    fieldErrors,
  };
}
